// =============================================================================
// SIREPPAR - Cuenta Bancaria Partidaria (Art. 40 Ley XII-24)
// =============================================================================

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { cuentaBancariaService, partidoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// =============================================================================
// Tipos y validación
// =============================================================================

interface CuentaBancaria {
  id: string;
  partidoId: string;
  cbu: string;
  banco: string;
  titular: string;
  sucursal: string | null;
  fechaApertura: string;
  fechaBaja: string | null;
  activa: boolean;
  creadoEn: string;
  actualizadoEn: string;
}

interface CuentaBancariaResult {
  activa: CuentaBancaria | null;
  historial: CuentaBancaria[];
}

const cuentaSchema = z.object({
  cbu: z.string().regex(/^\d{22}$/, 'El CBU debe tener exactamente 22 dígitos numéricos'),
  banco: z.string().min(2, 'Requerido').max(100),
  titular: z.string().min(2, 'Requerido').max(200),
  sucursal: z.string().max(100).optional(),
  fechaApertura: z.string().min(1, 'Requerida'),
});

type CuentaFormData = z.infer<typeof cuentaSchema>;

// =============================================================================
// Helpers
// =============================================================================

function formatFecha(fecha: string | null): string {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatCBU(cbu: string): string {
  // Agrupa en bloques de 4 para legibilidad
  return cbu.replace(/(\d{4})(?=\d)/g, '$1 ');
}

// =============================================================================
// Componente
// =============================================================================

export default function CuentaBancariaPage() {
  const { partidoId } = useParams<{ partidoId: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);

  const puedeEditar = ['ADMINISTRADOR', 'SUPERVISOR', 'APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE'].includes(user?.rol || '');

  // Info del partido (para mostrar nombre)
  const { data: partido } = useQuery({
    queryKey: ['partido', partidoId],
    queryFn: async () => {
      if (!partidoId) return null;
      const response = await partidoService.getById(partidoId);
      return response.data.data;
    },
    enabled: !!partidoId,
  });

  // Cuenta bancaria actual + historial
  const { data, isLoading, error } = useQuery<CuentaBancariaResult>({
    queryKey: ['cuenta-bancaria', partidoId],
    queryFn: async () => {
      if (!partidoId) throw new Error('partidoId requerido');
      const res = await cuentaBancariaService.getByPartido(partidoId);
      return res.data;
    },
    enabled: !!partidoId,
  });

  const activa = data?.activa ?? null;
  const historial = data?.historial ?? [];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CuentaFormData>({
    resolver: zodResolver(cuentaSchema),
    defaultValues: { cbu: '', banco: '', titular: '', sucursal: '', fechaApertura: '' },
  });

  const createMutation = useMutation({
    mutationFn: async (formData: CuentaFormData) => {
      if (!partidoId) throw new Error('partidoId requerido');
      return cuentaBancariaService.create(partidoId, formData);
    },
    onSuccess: () => {
      toast.success(activa ? 'Cuenta actualizada (la anterior quedó en historial)' : 'Cuenta declarada');
      queryClient.invalidateQueries({ queryKey: ['cuenta-bancaria', partidoId] });
      setShowModal(false);
      reset();
    },
    onError: (err: any) => {
      const mensaje = err?.response?.data?.error?.message || 'No se pudo guardar la cuenta bancaria';
      toast.error(mensaje);
    },
  });

  const onSubmit = (formData: CuentaFormData) => {
    createMutation.mutate(formData);
  };

  const closeModal = () => {
    if (createMutation.isPending) return;
    setShowModal(false);
    reset();
  };

  if (!partidoId) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">Falta el ID del partido en la URL.</p>
        <Link to="/financiamiento" className="text-sm text-blue-600 hover:underline">← Volver a Financiamiento</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/financiamiento" className="text-sm text-blue-600 hover:underline inline-block mb-2">
          ← Volver a Financiamiento
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Cuenta Bancaria</h1>
        {partido && (
          <p className="mt-1 text-sm text-gray-500">
            {(partido as any).denominacion} ({(partido as any).sigla}) · Art. 40 Ley XII-24
          </p>
        )}
      </div>

      {isLoading && (
        <div className="bg-white rounded-lg shadow p-6 text-center text-sm text-gray-500">
          Cargando...
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
          No se pudo cargar la información de la cuenta bancaria.
        </div>
      )}

      {/* Cuenta activa */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Cuenta activa</h2>
            {puedeEditar && (
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
              >
                {activa ? 'Actualizar cuenta' : 'Declarar cuenta'}
              </button>
            )}
          </div>

          <div className="p-6">
            {activa ? (
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">CBU</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{formatCBU(activa.cbu)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Banco</dt>
                  <dd className="mt-1 text-sm text-gray-900">{activa.banco}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Titular</dt>
                  <dd className="mt-1 text-sm text-gray-900">{activa.titular}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Sucursal</dt>
                  <dd className="mt-1 text-sm text-gray-900">{activa.sucursal || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Fecha de apertura</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatFecha(activa.fechaApertura)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Declarada el</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatFecha(activa.creadoEn)}</dd>
                </div>
              </dl>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500">
                  Este partido todavía no declaró una cuenta bancaria.
                </p>
                <p className="mt-2 text-xs text-gray-400">
                  Art. 48 Ley XII-24: sin cuenta declarada no se paga el aporte para desenvolvimiento institucional.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Historial */}
      {!isLoading && historial.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Historial</h2>
            <p className="mt-0.5 text-xs text-gray-500">Cuentas anteriores dadas de baja.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CBU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Banco</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titular</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Apertura</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Baja</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {historial.map((c) => (
                  <tr key={c.id}>
                    <td className="px-6 py-3 font-mono text-gray-900">{formatCBU(c.cbu)}</td>
                    <td className="px-6 py-3 text-gray-900">{c.banco}</td>
                    <td className="px-6 py-3 text-gray-900">{c.titular}</td>
                    <td className="px-6 py-3 text-gray-500">{formatFecha(c.fechaApertura)}</td>
                    <td className="px-6 py-3 text-gray-500">{formatFecha(c.fechaBaja)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de alta / reemplazo */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {activa ? 'Actualizar cuenta bancaria' : 'Declarar cuenta bancaria'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              {activa && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-900">
                  <strong>Atención:</strong> declarar una cuenta nueva dará de baja la actual
                  ({formatCBU(activa.cbu)}) con fecha de hoy. La cuenta anterior quedará en el historial.
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CBU <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={22}
                  {...register('cbu')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="22 dígitos sin espacios"
                />
                {errors.cbu && <p className="mt-1 text-xs text-red-600">{errors.cbu.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Banco <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  {...register('banco')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Banco del Chubut"
                />
                {errors.banco && <p className="mt-1 text-xs text-red-600">{errors.banco.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titular <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  {...register('titular')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Razón social del partido"
                />
                {errors.titular && <p className="mt-1 text-xs text-red-600">{errors.titular.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal</label>
                <input
                  type="text"
                  {...register('sucursal')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Opcional"
                />
                {errors.sucursal && <p className="mt-1 text-xs text-red-600">{errors.sucursal.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de apertura <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  {...register('fechaApertura')}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.fechaApertura && <p className="mt-1 text-xs text-red-600">{errors.fechaApertura.message}</p>}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={createMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {createMutation.isPending && (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                  )}
                  {createMutation.isPending ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
